import { useContext, useEffect, useMemo, useRef } from 'react';
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Tooltip,
  useMap
} from 'react-leaflet';
import MarkerClusterGroup from '@changey/react-leaflet-markercluster';
import 'leaflet.tilelayer.colorfilter';
import 'leaflet/dist/leaflet.css';
import '@changey/react-leaflet-markercluster/dist/styles.min.css';
import { AppContext } from 'providers/AppProvider';
import { OrderDataRegion } from 'api/dashboard/_request';
import L, { LatLngExpression } from 'leaflet';

interface ColorFilterOptions {
  transparent?: boolean;
  filter?: string[];
  attribution?: string | undefined;
}

const countryCoordinates: Record<string, LatLngExpression> = {
  India: [20.5937, 78.9629],
  'Saudi Arabia': [23.8859, 45.0792],
  UAE: [23.4241, 53.8478],
  USA: [37.0902, -95.7129],
  Germany: [51.1657, 10.4515],
  France: [46.6034, 1.8883],
  UK: [55.3781, -3.436],
  Canada: [56.1304, -106.3468]
};

const countryCodeMap: Record<string, string> = {
  India: 'in',
  'Saudi Arabia': 'sa',
  UAE: 'ae',
  USA: 'us',
  Germany: 'de',
  France: 'fr',
  UK: 'gb',
  Canada: 'ca',
  default: 'us'
};

const ALWAYS_HIGHLIGHT = ['India', 'Saudi Arabia', 'UAE'];

const LayerComponent = ({ data }: { data: OrderDataRegion[] }) => {
  const map = useMap();
  const { config } = useContext(AppContext);
  const { theme } = config;

  const filter =
    theme === 'dark'
      ? [
          'invert:98%',
          'grayscale:69%',
          'bright:89%',
          'contrast:111%',
          'hue:205deg',
          'saturate:1000%'
        ]
      : ['bright:101%', 'contrast:101%', 'hue:23deg', 'saturate:225%'];

  const countriesToHighlight = useMemo(() => {
    const dynamicCountries = data
      .map(item => item.country)
      .filter(Boolean) as string[];
    return Array.from(new Set([...ALWAYS_HIGHLIGHT, ...dynamicCountries]));
  }, [data]);

  const markers = useMemo(() => {
    return countriesToHighlight
      .filter(country => countryCoordinates[country])
      .map(country => ({
        country,
        position: countryCoordinates[country],
        data: data.find(d => d.country === country),
        isAlways: ALWAYS_HIGHLIGHT.includes(country)
      }));
  }, [countriesToHighlight, data]);

  useEffect(() => {
    map.invalidateSize();
  }, [config]);

  useEffect(() => {
    if (map) {
      (
        L.tileLayer as unknown as {
          colorFilter: (
            url: string,
            options: ColorFilterOptions
          ) => L.TileLayer;
        }
      )
        .colorFilter(
          'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
          {
            transparent: true,
            filter,
            attribution: undefined
          }
        )
        .addTo(map);
    }
  }, [theme]);

  return (
    <>
      <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />
      <MarkerClusterGroup chunkedLoading spiderfyOnMaxZoom={false}>
        {markers.map(({ country, position, data, isAlways }) => (
          <CircleMarker
            key={country}
            center={position}
            radius={isAlways ? 28 : 18}
            pathOptions={{
              color: isAlways ? '#339AF0' : '#FF4B2B',
              fillColor: isAlways ? '#74C0FC' : '#FF6B6B',
              fillOpacity: 0.65,
              weight: 2
            }}
            eventHandlers={{
              add: e => {
                // Animate radius on pulse using JS (optional)
                const el = e.target._path;
                if (el) {
                  el.style.transition = 'r 1.2s ease-in-out';
                  let grow = true;
                  setInterval(() => {
                    el.setAttribute('r', grow ? '30' : isAlways ? '28' : '18');
                    grow = !grow;
                  }, 1200);
                }
              }
            }}
          >
            <Tooltip direction="top" offset={[0, -10]} permanent>
              <div style={{ textAlign: 'center' }}>
                <img
                  src={`https://flagcdn.com/w40/${
                    countryCodeMap[country] || 'ae'
                  }.png`}
                  alt={`${country} flag`}
                  width={20}
                  height={15}
                  style={{ marginBottom: 4 }}
                />
                <strong>{country}</strong>
                {data?.totalOrders && <div>🛒 {data.totalOrders} Orders</div>}
                {data?.totalUsers && <div>👤 {data.totalUsers} Customers</div>}
                {data?.totalProducts && (
                  <div>📦 {data.totalProducts} Products</div>
                )}
              </div>
            </Tooltip>
          </CircleMarker>
        ))}
      </MarkerClusterGroup>
    </>
  );
};

const EcomTopRegionsMap = ({ data, ...rest }: { data: OrderDataRegion[] }) => {
  const center: LatLngExpression = [20, 30];
  const {
    config: { isRTL }
  } = useContext(AppContext);

  return (
    <MapContainer
      zoom={isRTL ? 1.8 : 1.5}
      minZoom={isRTL ? 1.8 : 1.3}
      zoomSnap={0.5}
      center={center}
      {...rest}
      className="h-100 bg-body-emphasis"
    >
      <LayerComponent data={data} />
    </MapContainer>
  );
};

export default EcomTopRegionsMap;
